import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  UserButton,
} from '@clerk/clerk-react';
import { AuthenticatedApolloProvider } from './lib/apollo-auth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Navigation } from './components/Navigation';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { OrganizationSwitcher } from './components/OrganizationSwitcher';
import { ProjectSidebar } from './components/ProjectSidebar';
import { HomePage } from './pages/HomePage';
import { ContextDetailPage } from './pages/ContextDetailPage';
import { GraphPage } from './pages/GraphPage';
import { ConflictsPage } from './pages/ConflictsPage';
import { PricingPage } from './pages/PricingPage';
import { SettingsPage } from './pages/SettingsPage';
import { TeamManagementPage } from './pages/TeamManagementPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminOrganizationsPage } from './pages/AdminOrganizationsPage';
import { AdminPromosPage } from './pages/AdminPromosPage';
import { AdminAnalyticsPage } from './pages/AdminAnalyticsPage';
import { AdminAuditLogPage } from './pages/AdminAuditLogPage';
import { AdminTeamPage } from './pages/AdminTeamPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';

function App() {
  return (
    <ErrorBoundary>
      <Router>
        {/* Legal pages - accessible to everyone */}
        <Routes>
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        </Routes>

        {/* Show app when signed in */}
        {/* @ts-ignore Clerk SignedIn typing is slightly out of sync with React 19, but works at runtime */}
        <SignedIn>
          <AuthenticatedApolloProvider>
            <Routes>
              {/* Admin Panel Routes - Separate layout with AdminLayout */}
              <Route path="/admin/*" element={
                <Routes>
                  <Route path="/" element={<AdminDashboardPage />} />
                  <Route path="/users" element={<AdminUsersPage />} />
                  <Route path="/orgs" element={<AdminOrganizationsPage />} />
                  <Route path="/promos" element={<AdminPromosPage />} />
                  <Route path="/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="/audit" element={<AdminAuditLogPage />} />
                  <Route path="/team" element={<AdminTeamPage />} />
                </Routes>
              } />

              {/* Regular App Routes - With main layout */}
              <Route path="/*" element={
                <OrganizationProvider>
                  <div className="min-h-screen bg-gray-50">
                    {/* Header with logo, org switcher, and user button */}
                    <header className="bg-white shadow-sm">
                      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                          <h1 className="text-xl font-bold text-blue-600">ContextLoom</h1>
                          <OrganizationSwitcher />
                        </div>
                        <UserButton afterSignOutUrl="/sign-in" />
                      </div>
                    </header>

                    <div className="flex">
                      {/* Project Sidebar */}
                      <ProjectSidebar />

                      {/* Main Content Area */}
                      <div className="flex-1">
                        <Navigation />
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/context/:id" element={<ContextDetailPage />} />
                          <Route path="/graph" element={<GraphPage />} />
                          <Route path="/conflicts" element={<ConflictsPage />} />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/team" element={<TeamManagementPage />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </div>
                    </div>
                  </div>
                </OrganizationProvider>
              } />
            </Routes>
          </AuthenticatedApolloProvider>
        </SignedIn>

        {/* Show sign in/up when signed out */}
        {/* @ts-ignore Clerk SignedOut typing is slightly out of sync with React 19, but works at runtime */}
        <SignedOut>
          <Routes>
            <Route
              path="/sign-in/*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <SignIn routing="path" path="/sign-in" />
                </div>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <SignUp routing="path" path="/sign-up" />
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/sign-in" replace />} />
          </Routes>
        </SignedOut>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
